import { useCallback, useMemo } from 'react'

import { useFeatureFlag } from '@/services/feature/useFeatureQueries'

import { LES_INBOUND_CANCEL_BLOCK_FLAG, LES_ORIGIN_CANCEL_BLOCKED_MESSAGE, LesOriginCandidate, hasLesOriginRow } from './index'

export interface LesInboundCancelGate {
  /** Feature Flag 가 켜져 차단이 동작하는가. */
  enabled: boolean
  /** 선택한 행으로 취소를 막아야 하는가. */
  isBlocked: (rows: readonly LesOriginCandidate[]) => boolean
  /** 막았을 때 보여 줄 문구. 막지 않으면 `null`. */
  blockedReason: (rows: readonly LesOriginCandidate[]) => string | null
}

/**
 * LES 유래 입고건 취소 차단 게이트. (LOGISPRD-12332)
 *
 * BE 차단은 `feature.lesInboundCancel.block` 이 ON 일 때만 걸린다. 화면이 flag 를 안 보고 상시 막으면
 * flag OFF 인 환경에서 서버는 취소를 받아 주는데 버튼만 죽어 AS-IS 가 깨진다 — 그래서 같은 flag 를 본다.
 *
 * flag 조회가 실패하면 `getFeatureFlag` 가 `false` 로 흡수한다(fail-safe). 그때는 차단이 풀리고 서버
 * 가드가 최종 방어선으로 남는다 — BE 와 같은 방향의 fail-safe 다.
 */
export const useLesInboundCancelGate = (): LesInboundCancelGate => {
  const { data: enabled = false } = useFeatureFlag(LES_INBOUND_CANCEL_BLOCK_FLAG)

  const isBlocked = useCallback((rows: readonly LesOriginCandidate[]) => enabled && rows.length > 0 && hasLesOriginRow(rows), [enabled])

  const blockedReason = useCallback(
    (rows: readonly LesOriginCandidate[]) => (isBlocked(rows) ? LES_ORIGIN_CANCEL_BLOCKED_MESSAGE : null),
    [isBlocked],
  )

  return useMemo(() => ({ enabled, isBlocked, blockedReason }), [blockedReason, enabled, isBlocked])
}
